import React, { FC, ComponentType, ComponentProps } from 'react';
import { snakeToCamel } from '../caseUtils';
import { UserContextType, UserCustomWindow } from './user.types';
import { USER_ONBOARDING } from '../../../shared/constants/userOnboardingConstants';
import { USER_CURRENT_STATUS } from '../../../shared/constants/userStatusConstants';
import { isClientSide } from '../utils/ssrUtils';

declare let window: UserCustomWindow;

const getUserName = (currentUser: UserContextType['currentUser']): string => {
  if (currentUser.firstName) {
    return currentUser.firstName;
  }
  if (currentUser.userName) {
    return currentUser.userName;
  }
  return '';
};

const getUser = (): UserContextType['currentUser'] => {
  const currentUser = (isClientSide && snakeToCamel(window.current_user)) || {};
  return {
    ...currentUser,
    name: getUserName(currentUser),
    isExpired: currentUser.status === USER_CURRENT_STATUS.EXPIRED_FREE_TRIAL,
    isOnboardedOnWatchers:
      currentUser.wasOnboarded &&
      currentUser.wasOnboarded.includes(USER_ONBOARDING.SEE_WATCHERS_INBOX),
    isIcp:
      isClientSide &&
      window.METIERS_IS_ICP &&
      !!currentUser.metier &&
      window.METIERS_IS_ICP.includes(currentUser.metier),
  };
};

const staticUserContext: UserContextType = {
  currentUser: getUser(),
  email: isClientSide ? window.email : '',
  loggedIn: isClientSide && window.logged_in,
  accessPremium: isClientSide && window.access_premium,
  REFERRAL_AMOUNT: isClientSide ? window.REFERRAL_AMOUNT : null,
  setCurrentUserName: () => {},
  setSeeWatchersInbox: () => {},
};

export const UserContext = React.createContext(staticUserContext);

type State = {
  currentUser: UserContextType['currentUser'];
};

export class UserProvider extends React.Component<{}, State> {
  state: State = {
    currentUser: staticUserContext.currentUser,
  };

  setCurrentUserName = (firstName: string, lastName: string) => {
    this.setState(state => ({
      currentUser: {
        ...state.currentUser,
        rights: [...state.currentUser.rights],
        abTestsBuckets: [...state.currentUser.abTestsBuckets],
        wasOnboarded: [...state.currentUser.wasOnboarded],
        firstName,
        lastName,
        userName: `${firstName} ${lastName}`,
        name: firstName,
      },
    }));
  };

  setSeeWatchersInbox = () => {
    this.setState(state => ({
      currentUser: {
        ...state.currentUser,
        rights: [...state.currentUser.rights],
        abTestsBuckets: [...state.currentUser.abTestsBuckets],
        wasOnboarded: [
          ...state.currentUser.wasOnboarded,
          USER_ONBOARDING.SEE_WATCHERS_INBOX,
        ],
        isOnboardedOnWatchers: true,
      },
    }));
  };

  render() {
    const {
      setCurrentUserName,
      setSeeWatchersInbox,
      state: { currentUser },
    } = this;
    return (
      <UserContext.Provider
        value={{
          ...staticUserContext,
          currentUser,
          setCurrentUserName,
          setSeeWatchersInbox,
        }}
      >
        {this.props.children}
      </UserContext.Provider>
    );
  }
}

type Subtract<T extends K, K> = Omit<T, keyof K>;

export function withUser<
  P extends ComponentType<ComponentProps<P> & UserContextType>,
  ResolvedProps = JSX.LibraryManagedAttributes<
    P,
    Subtract<React.ComponentProps<P>, UserContextType>
  >
>(WrappedComponent: P) {
  const ComponentWithUser: FC<ResolvedProps> = props => (
    <UserContext.Consumer>
      {userContext => (
        <WrappedComponent
          {...(props as JSX.LibraryManagedAttributes<
            P,
            React.ComponentProps<P>
          >)}
          {...userContext}
        />
      )}
    </UserContext.Consumer>
  );
  return ComponentWithUser;
}
